import React, { useRef, useEffect, useState, useCallback } from 'react';
import PunchPointsModal from './PunchPointsModal';
import PropTypes from 'prop-types';
import { Form, Button, Row, Col, Table, Tabs, Tab, Modal, Alert } from 'react-bootstrap';
import { IoInformationCircleOutline } from 'react-icons/io5';
import { useParams, useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { useForm, useWatch } from 'react-hook-form';
import SignatureCanvas from 'react-signature-canvas';
import { SUBMIT_FORM, VIEW_FORM } from 'actions/formActions';
import { toast } from 'react-toastify';
import _ from 'lodash';
import { cookie } from 'utils/cookies';

const VCBForm = ({ role = cookie.get('user-role'), userId = cookie.get('user-id') }) => {
  const dispatch = useDispatch();
  const { viewFormData } = useSelector((state) => state.checklist);
  const { formId, stationId } = useParams();
  const sigCanvas = useRef(null);
  const navigate = useNavigate();
  const [showModal, setShowModal] = useState(false);
  const [showPunchModal, setShowPunchModal] = useState(false);
  const [pendingRework, setPendingRework] = useState(null);
  const [currentSignature, setCurrentSignature] = useState({ level: '', role: '' });
  const [isLoading, setIsLoading] = useState(true);
  const dataLoadedRef = useRef(false);

  const { register, control, handleSubmit, setValue, getValues } = useForm({
    defaultValues: {
      station_id: stationId || '',
      project_name: '',
      equipment_manufacturer: '',
      rated_output: '',
      serial_number: '',
      equipment_tag: '',
      visual_checks: Array(13).fill({ status: '', remark: '' }),
      physical_checks: Array(7).fill({ status: '', remark: '' }),
      l1_comments: {
        contractor: { name: '', company: '', signature: '', signatureType: 'upload', date: '' },
        charge_engineer: { name: '', company: '', signature: '', signatureType: 'upload', date: '' },
        quality: { name: '', company: '', signature: '', signatureType: 'upload', date: '' },
        optional: { name: '', company: '', signature: '', signatureType: 'upload', date: '' },
      },
      l1_other_comments: '',
      l1_status: 'draft',
      functional_checks_status: '',
      testing_commissioning_status: '',
      vcb_healthy: '',
      functional_checks: Array(7).fill({ status: '', remark: '' }),
      insulation_resistance_test: '',
      insulation_resistance_results: Array(6).fill({ phase: '', value: '', remark: '' }),
      insulation_resistance_breaker_off: Array(3).fill({ phase: '', value: '', remark: '' }),
      relay_setting_a: Array(3).fill({ setting: '', value: '', remark: '' }),
      relay_setting_b: Array(3).fill({ setting: '', value: '', remark: '' }),
      relay_setting_c: Array(3).fill({ setting: '', value: '', remark: '' }),
      relay_setting_d: Array(3).fill({ setting: '', value: '', remark: '' }),

      instruments: [
        { description: 'IR Tester (5kV Recommended)', serial: '', range: '', calibration: '' },
        { description: 'Digital Multi meter / Tong Tester', serial: '', range: '', calibration: '' },
      ],
      observations: { a: '', b: '', c: '' },
      l2_comments: {
        contractor: { name: '', company: '', signature: '', signatureType: 'upload', date: '' },
        charge_engineer: { name: '', company: '', signature: '', signatureType: 'upload', date: '' },
        quality: { name: '', company: '', signature: '', signatureType: 'upload', date: '' },
        optional: { name: '', company: '', signature: '', signatureType: 'upload', date: '' },
      },
      l2_office_comments: '',
      l2_other_comments: '',
      l2_status: 'draft',
      status: 'draft',
      punch_points: '',
    },
  });

  const formValues = useWatch({ control });
  const punch_points = useWatch({ control, name: 'punch_points' });
  const l1_status = useWatch({ control, name: 'l1_status' });
  const l2_status = useWatch({ control, name: 'l2_status' });
  const l1_manager_id = useWatch({ control, name: 'l1_manager_id' });
  const l2_manager_id = useWatch({ control, name: 'l2_manager_id' });

  // ---- Per-level editability for core fields ----
  const isLevel1Editable = (!l1_manager_id || l1_manager_id === userId) && ((role === 'engineer' && (l1_status === 'draft' || l1_status === 'CONTRACTOR' || l1_status === 'MANAGER')) || (role === 'contractor' && (l1_status === 'draft' || l1_status === 'CONTRACTOR')));
  const isLevel2Editable = (!l2_manager_id || l2_manager_id === userId) && ((role === 'engineer' && (l2_status === 'draft' || l2_status === 'CONTRACTOR' || l2_status === 'MANAGER')) || (role === 'contractor' && (l2_status === 'draft' || l2_status === 'CONTRACTOR')));

  // ---- General comments open for ALL roles until that level is approved ----
  const canEditL1General = l1_status !== 'approved';
  const canEditL2General = l2_status !== 'approved';

  // ---- Contractor tab gating ----
  const disableLevel1Tab = role === 'contractor' && getValues('l1_status') !== 'draft' && getValues('l1_status') !== 'CONTRACTOR';
  const disableLevel2Tab = role === 'contractor' && getValues('l2_status') !== 'draft' && getValues('l2_status') !== 'CONTRACTOR';

  // ---- Signature edit permissions (per level, per role, per stage) ----
  const canEditSignature = useCallback(
    (level, sigRole) => {
      const managerId = level === 'l1' ? getValues('l1_manager_id') : getValues('l2_manager_id');
      if (managerId && managerId !== userId) return false;
      const s = level === 'l1' ? getValues('l1_status') : getValues('l2_status');
      if (sigRole === 'charge_engineer' && role === 'engineer' && (s === 'draft' || s === 'CONTRACTOR' || s === 'MANAGER')) return true;
      if (sigRole === 'contractor' && role === 'contractor' && (s === 'draft' || s === 'CONTRACTOR')) return true;
      if (sigRole === 'quality' && role === 'quality' && s === 'QUALITY') return true;
      return false;
    },
    [role, getValues, userId]
  );

  // ---- Overall status derivation across both levels ----
  const deriveCommonStatus = (s1, s2) => {
    if (s1 === 'CONTRACTOR' && s2 === 'CONTRACTOR') return 'CONTRACTOR';
    if (s1 === 'MANAGER' && s2 === 'MANAGER') return 'MANAGER';
    if (s1 === 'QUALITY' && s2 === 'QUALITY') return 'QUALITY';
    if (s1 === 'approved' && s2 === 'approved') return 'approved';
    return 'draft';
  };

  // ---- Autosave ----
  const autoSave = useCallback(
    _.debounce((data) => {
      if (!dataLoadedRef.current) return;
      const overall = deriveCommonStatus(data.l1_status, data.l2_status);
      const payload = {
        form_data: {
          status: overall,
          form_id: formId,
          formName: 'vcb',
          stationId: data.station_id,
          project_name: data.project_name,
          equipment_manufacturer: data.equipment_manufacturer,
          rated_output: data.rated_output,
          serial_number: data.serial_number,
          equipment_tag: data.equipment_tag,
          l1: {
            visual_checks: data.visual_checks,
            physical_checks: data.physical_checks,
            comments: data.l1_comments,
            other_comments: data.l1_other_comments,
            status: data.l1_status,
          },
          l2: {
            functional_checks_status: data.functional_checks_status,
            testing_commissioning_status: data.testing_commissioning_status,
            vcb_healthy: data.vcb_healthy,
            functional_checks: data.functional_checks,
            insulation_resistance_test: data.insulation_resistance_test,
            insulation_resistance_results: data.insulation_resistance_results,
            insulation_resistance_breaker_off: data.insulation_resistance_breaker_off,
            relay_setting_a: data.relay_setting_a,
            relay_setting_b: data.relay_setting_b,
            relay_setting_c: data.relay_setting_c,
            relay_setting_d: data.relay_setting_d,

            instruments: data.instruments,
            observations: data.observations,
            comments: data.l2_comments,
            office_comments: data.l2_office_comments,
            other_comments: data.l2_other_comments,
            status: data.l2_status,
          },
          punch_points: data.punch_points,
        },
      };
      dispatch({ type: SUBMIT_FORM, payload: { data: payload.form_data } });
    }, 2000),
    [dispatch, formId]
  );

  useEffect(() => { autoSave(formValues); }, [formValues, autoSave]);

  // ---- Load existing ----
  useEffect(() => {
    if (formId) {
      dispatch({ type: VIEW_FORM, payload: { formId, formName: 'vcb' } });
    }
    setIsLoading(false);
  }, [dispatch, formId]);

  useEffect(() => {
    if (viewFormData) {
      const data = viewFormData || {};
      setValue('station_id', data.stationId || stationId || '');
      setValue('project_name', data.project_name || '');
      setValue('equipment_manufacturer', data.equipment_manufacturer || '');
      setValue('rated_output', data.rated_output || '');
      setValue('serial_number', data.serial_number || '');
      setValue('equipment_tag', data.equipment_tag || '');
      setValue('visual_checks', Array.isArray(data.l1?.visual_checks) ? data.l1.visual_checks : Array(13).fill({ status: '', remark: '' }));
      setValue('physical_checks', Array.isArray(data.l1?.physical_checks) ? data.l1.physical_checks : Array(7).fill({ status: '', remark: '' }));
      setValue('l1_comments', data.l1?.comments || {
        contractor: { name: '', company: '', signature: '', signatureType: 'upload', date: '' },
        charge_engineer: { name: '', company: '', signature: '', signatureType: 'upload', date: '' },
        quality: { name: '', company: '', signature: '', signatureType: 'upload', date: '' },
        optional: { name: '', company: '', signature: '', signatureType: 'upload', date: '' },
      });
      setValue('l1_other_comments', data.l1?.other_comments || '');
      setValue('l1_status', data.l1?.status || 'draft');
      setValue('l1_manager_id', data.l1?.managerId || null);

      setValue('functional_checks_status', data.l2?.functional_checks_status || '');
      setValue('testing_commissioning_status', data.l2?.testing_commissioning_status || '');
      setValue('vcb_healthy', data.l2?.vcb_healthy || '');
      setValue('functional_checks', Array.isArray(data.l2?.functional_checks) ? data.l2.functional_checks : Array(7).fill({ status: '', remark: '' }));
      setValue('insulation_resistance_test', data.l2?.insulation_resistance_test || '');
      setValue('insulation_resistance_results', Array.isArray(data.l2?.insulation_resistance_results) ? data.l2.insulation_resistance_results : Array(6).fill({ phase: '', value: '', remark: '' }));

      setValue('insulation_resistance_breaker_off', Array.isArray(data.l2?.insulation_resistance_breaker_off) ? data.l2.insulation_resistance_breaker_off : Array(3).fill({ phase: '', value: '', remark: '' }));
      setValue('relay_setting_a', Array.isArray(data.l2?.relay_setting_a) ? data.l2.relay_setting_a : Array(3).fill({ setting: '', value: '', remark: '' }));
      setValue('relay_setting_b', Array.isArray(data.l2?.relay_setting_b) ? data.l2.relay_setting_b : Array(3).fill({ setting: '', value: '', remark: '' }));
      setValue('relay_setting_c', Array.isArray(data.l2?.relay_setting_c) ? data.l2.relay_setting_c : Array(3).fill({ setting: '', value: '', remark: '' }));
      setValue('relay_setting_d', Array.isArray(data.l2?.relay_setting_d) ? data.l2.relay_setting_d : Array(3).fill({ setting: '', value: '', remark: '' }));

      setValue('instruments', Array.isArray(data.l2?.instruments)
        ? data.l2.instruments
        : [
          { description: 'IR Tester (5kV Recommended)', serial: '', range: '', calibration: '' },
          { description: 'Digital Multi meter / Tong Tester', serial: '', range: '', calibration: '' },
        ]);
      setValue('observations', data.l2?.observations || { a: '', b: '', c: '' });
      setValue('l2_comments', data.l2?.comments || {
        contractor: { name: '', company: '', signature: '', signatureType: 'upload', date: '' },
        charge_engineer: { name: '', company: '', signature: '', signatureType: 'upload', date: '' },
        quality: { name: '', company: '', signature: '', signatureType: 'upload', date: '' },
        optional: { name: '', company: '', signature: '', signatureType: 'upload', date: '' },
      });
      setValue('l2_office_comments', data.l2?.office_comments || '');
      setValue('l2_other_comments', data.l2?.other_comments || '');
      setValue('l2_status', data.l2?.status || 'draft');
      setValue('l2_manager_id', data.l2?.managerId || null);
      setValue('status', data.status || 'draft');
      setValue('punch_points', viewFormData?.punch_points || '');
      dataLoadedRef.current = true;
    }
  }, [viewFormData, stationId, setValue]);

  // ---- Signature helpers (respect role+status gating) ----
  const handleImageUpload = (e, level, sigRole) => {
    if (!canEditSignature(level, sigRole)) return toast.info('This signature is not editable for your role/stage');
    const file = e.target.files[0];
    const maxSizeInBytes = 0.05 * 1024 * 1024; // 50KB
    if (file) {
      if (file.size > maxSizeInBytes) {
        toast.error('File size exceeds 50KB. Please upload a smaller image.');
        handleResetSignature(level, sigRole);
        sigCanvas.current?.clear();
        return;
      }
      const reader = new FileReader();
      reader.onloadend = () => setValue(`${level}_comments.${sigRole}.signature`, reader.result, { shouldDirty: true, shouldValidate: true });
      reader.readAsDataURL(file);
    }
  };

  const handleSignatureTypeChange = (level, sigRole, type) => {
    if (!canEditSignature(level, sigRole)) return toast.info('This signature is not editable for your role/stage');
    setValue(`${level}_comments.${sigRole}.signatureType`, type, { shouldDirty: true, shouldValidate: true });
    setValue(`${level}_comments.${sigRole}.signature`, '', { shouldDirty: true, shouldValidate: true });
    if (type === 'digital') {
      setCurrentSignature({ level, role: sigRole });
      setShowModal(true);
    }
  };

  const handleResetSignature = (level, sigRole) => {
    if (!canEditSignature(level, sigRole)) return toast.info('This signature is not editable for your role/stage');
    setValue(`${level}_comments.${sigRole}.signature`, '', { shouldDirty: true, shouldValidate: true });
    setValue(`${level}_comments.${sigRole}.signatureType`, 'upload', { shouldDirty: true, shouldValidate: true });
  };

  const handleSaveSignature = () => {
    const { level, role: sigRole } = currentSignature;
    if (!canEditSignature(level, sigRole)) {
      setShowModal(false);
      return;
    }
    if (sigCanvas.current && !sigCanvas.current.isEmpty()) {
      const signatureData = sigCanvas.current.toDataURL();
      setValue(`${level}_comments.${sigRole}.signature`, signatureData, { shouldDirty: true, shouldValidate: true });
    }
    setShowModal(false);
    sigCanvas.current?.clear();
  };

  const handleClearSignature = () => {
    sigCanvas.current?.clear();
  };

  const handleCloseModal = () => {
    setShowModal(false);
    sigCanvas.current?.clear();
  };

  // ---- Submit transitions (per level) ----
  const nextStatusFor = (curr, action) => {
    // action: 'pending', 'approved', 'draft'
    if (role === 'engineer') return action === 'draft' ? 'draft' : 'CONTRACTOR';
    if (role === 'contractor') return action === 'draft' ? 'CONTRACTOR' : 'MANAGER';
    if (role === 'manager') return action === 'approved' ? 'QUALITY' : 'draft';
    if (role === 'quality') return action === 'approved' ? 'approved' : 'draft';
    return curr || 'draft';
  };

  const onSubmit = (level, action) => (data) => {
    const s1 = data.l1_status;
    const s2 = data.l2_status;
    const l1_next = level === 'l1' ? nextStatusFor(s1, action) : s1;
    const l2_next = level === 'l2' ? nextStatusFor(s2, action) : s2;
    const overall = deriveCommonStatus(l1_next, l2_next);

    const payload = {
      form_data: {
        status: overall,
        form_id: formId,
        formName: 'vcb',
        stationId: data.station_id,
        project_name: data.project_name,
        equipment_manufacturer: data.equipment_manufacturer,
        rated_output: data.rated_output,
        serial_number: data.serial_number,
        equipment_tag: data.equipment_tag,
        l1: {
          visual_checks: data.visual_checks,
          physical_checks: data.physical_checks,
          comments: data.l1_comments,
          other_comments: data.l1_other_comments,
          status: l1_next,
        },
        l2: {
          functional_checks_status: data.functional_checks_status,
          testing_commissioning_status: data.testing_commissioning_status,
          vcb_healthy: data.vcb_healthy,
          functional_checks: data.functional_checks,
          insulation_resistance_test: data.insulation_resistance_test,
          insulation_resistance_results: data.insulation_resistance_results,
            insulation_resistance_breaker_off: data.insulation_resistance_breaker_off,
            relay_setting_a: data.relay_setting_a,
            relay_setting_b: data.relay_setting_b,
            relay_setting_c: data.relay_setting_c,
            relay_setting_d: data.relay_setting_d,

          instruments: data.instruments,
          observations: data.observations,
          comments: data.l2_comments,
          office_comments: data.l2_office_comments,
          other_comments: data.l2_other_comments,
          status: l2_next,
        },
        punch_points: data.punch_points,
      },
    };
    dispatch({ type: SUBMIT_FORM, payload: { data: payload.form_data } });
    const label = action === 'approved' ? 'Approved' : action === 'draft' ? 'Saved as Draft' : 'Submitted';
    toast.success(`Level ${level.toUpperCase()} ${label} Successfully`);
    navigate(-1);
  };

  // ---- Button gating for submit/draft visibility ----
  const submitDisabledL1 = (role === 'engineer' || role === 'contractor') ? (l1_status !== 'draft' && l1_status !== 'CONTRACTOR') : true;
  const submitDisabledL2 = (role === 'engineer' || role === 'contractor') ? (l2_status !== 'draft' && l2_status !== 'CONTRACTOR') : true;

  const canEditPunchPoints = ['manager', 'quality', 'maintenance', 'operation'].includes((role || '').toLowerCase());
  const handleReworkClick = (levelOrAction, actionStr) => (e) => {
    if (e) e.preventDefault();
    if (actionStr) {
      setPendingRework({ level: levelOrAction, action: actionStr });
    } else {
      setPendingRework({ action: levelOrAction });
    }
    setShowPunchModal(true);
  };

  const handleSavePunchPoints = async () => {
    if (pendingRework) {
      setShowPunchModal(false);
      if (pendingRework.level) {
        handleSubmit(onSubmit(pendingRework.level, pendingRework.action))();
      } else {
        handleSubmit(onSubmit(pendingRework.action))();
      }
    }
  };

  if (isLoading) return <div>Loading...</div>;

  return (
    <div className="page-body container-xl">
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h2 className="page-title mb-4">VCB - Site Acceptance Testing</h2>
        <Button variant="outline-warning" size="sm" onClick={() => setShowPunchModal(true)}>
          💬 See Comments
        </Button>
      </div>

      <Form>
        <h3>1. Equipment Details</h3>
        <Row>
          <Col md={4}>
            <Form.Group className="mb-3">
              <Form.Label>Station ID</Form.Label>
              <Form.Control type="text" {...register('station_id')} readOnly />
            </Form.Group>
          </Col>
          <Col md={4}>
            <Form.Group className="mb-3">
              <Form.Label>Project Name</Form.Label>
              <Form.Control type="text" {...register('project_name')} disabled={!isLevel1Editable} />
            </Form.Group>
          </Col>
          <Col md={4}>
            <Form.Group className="mb-3">
              <Form.Label>Equipment Manufacturer</Form.Label>
              <Form.Control type="text" {...register('equipment_manufacturer')} disabled={!isLevel1Editable} />
            </Form.Group>
          </Col>
          <Col md={4}>
            <Form.Group className="mb-3">
              <Form.Label>Rated Output</Form.Label>
              <Form.Control type="text" {...register('rated_output')} disabled={!isLevel1Editable} />
            </Form.Group>
          </Col>
          <Col md={4}>
            <Form.Group className="mb-3">
              <Form.Label>Serial Number</Form.Label>
              <Form.Control type="text" {...register('serial_number')} disabled={!isLevel1Editable} />
            </Form.Group>
          </Col>
          <Col md={4}>
            <Form.Group className="mb-3">
              <Form.Label>Equipment Tag</Form.Label>
              <Form.Control type="text" {...register('equipment_tag')} disabled={!isLevel1Editable} />
            </Form.Group>
          </Col>
        </Row>

        {punch_points && (
          <Alert variant="warning" className="d-flex align-items-center justify-content-between shadow-sm border-0 mb-4" style={{ borderRadius: '12px', background: '#fff9db', color: '#856404' }}>
            <div className="d-flex align-items-center">
              <IoInformationCircleOutline className="me-2 text-warning" size={24} />
              <div>
                <strong className="d-block">Rework Comments Available</strong>
                <span className="small">Please review the comments from the manager/quality team.</span>
              </div>
            </div>
            <Button 
              variant="warning" 
              size="sm" 
              className="rounded-pill px-3 fw-bold shadow-sm"
              onClick={() => setShowPunchModal(true)}
            >
              View Comments
            </Button>
          </Alert>
        )}

        <Tabs defaultActiveKey="level1" id="sat-tabs" className="mb-3">
          <Tab eventKey="level1" title={`Level 1 - Pre-Commissioning (${l1_status})`} disabled={disableLevel1Tab}>
            <h3 className="mt-4">2. Visual Checks</h3>
            <Table bordered>
              <thead>
                <tr>
                  <th>Sr. No.</th>
                  <th>Description</th>
                  <th>Status</th>
                  <th>Remark</th>
                </tr>
              </thead>
              <tbody>
                {[
                  'Has the identification of equipment tags been done as per SLD?',
                  'Has the identification of the manufacturer’s nameplate been done as per GTP?',
                  'Has the VCB Vendor TC check been verified with the serial number mentioned in the remark?',
                  'Has the presence of gaskets on the panel doors been checked?',
                  'Has a visual check of lamps and indicators been conducted?',
                  'Has double earthing of the VCB been ensured?',
                  'Are the panel door locking arrangements in working condition?',
                  'Has the sealing of all unwanted holes, if available, been ensured?',
                  'Has the GTP/control wiring been properly affixed inside the panel?',
                  'Has the VCB location and orientation been verified as per design and drawing?',
                  'Has the earth jumper been installed between the VCB panel door and the VCB panel?',
                  'Has the danger plate been affixed on all sides of the VCB?',
                  'Has the tightness of the power cable termination been checked with proper marking on bolts and busbar?',
                ].map((desc, index) => (
                  <tr key={index}>
                    <td>{index + 1}</td>
                    <td>{desc}</td>
                    <td>
                      <Form.Check inline label="Yes" type="radio" name={`visual_checks_${index}`} value="Yes" {...register(`visual_checks.${index}.status`)} disabled={!isLevel1Editable} />
                      <Form.Check inline label="No" type="radio" name={`visual_checks_${index}`} value="No" {...register(`visual_checks.${index}.status`)} disabled={!isLevel1Editable} />
                      <Form.Check inline label="NA" type="radio" name={`visual_checks_${index}`} value="NA" {...register(`visual_checks.${index}.status`)} disabled={!isLevel1Editable} />
                    </td>
                    <td>
                      <Form.Control type="text" {...register(`visual_checks.${index}.remark`)} disabled={!isLevel1Editable} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>

            <h3 className="mt-4">3. Physical Checks</h3>
            <Table bordered>
              <thead>
                <tr>
                  <th>Sr. No.</th>
                  <th>Description</th>
                  <th>Status</th>
                  <th>Remark</th>
                </tr>
              </thead>
              <tbody>
                {[
                  'Has a physical check been conducted to ensure there is no loose control wiring and proper cable dressing?',
                  'Has a physical check been conducted to verify that internal components are installed properly?',
                  'Has the tightness of all cables and glands been checked?',
                  'Has the panel board been properly tightened on the base frame?',
                  'Has the earth strip/cable been properly tightened with the panel?',
                  'Has the correct method of cable termination and tagging been ensured?',
                  'Has the identification of the phase sequence inside the VCB been verified?',
                ].map((desc, index) => (
                  <tr key={index}>
                    <td>{index + 1}</td>
                    <td>{desc}</td>
                    <td>
                      <Form.Check inline label="Yes" type="radio" name={`physical_checks_${index}`} value="Yes" {...register(`physical_checks.${index}.status`)} disabled={!isLevel1Editable} />
                      <Form.Check inline label="No" type="radio" name={`physical_checks_${index}`} value="No" {...register(`physical_checks.${index}.status`)} disabled={!isLevel1Editable} />
                      <Form.Check inline label="NA" type="radio" name={`physical_checks_${index}`} value="NA" {...register(`physical_checks.${index}.status`)} disabled={!isLevel1Editable} />
                    </td>
                    <td>
                      <Form.Control type="text" {...register(`physical_checks.${index}.remark`)} disabled={!isLevel1Editable} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>

            <h3 className="mt-4">4. Level 1 Pre-Commissioning Comments</h3>
            <Table bordered>
              <thead>
                <tr>
                  <th></th>
                  <th>Engineer - Contractor</th>
                  <th>Engineer - Charge Zone</th>
                  <th>Quality - Charge Zone</th>
                  <th>Optional</th>
                </tr>
              </thead>
              <tbody>
                {['Name', 'Company', 'Signature', 'Date'].map((field) => (
                  <tr key={field}>
                    <td>{field}</td>
                    {['contractor', 'charge_engineer', 'quality', 'optional'].map((r) => {
                      const editable = canEditSignature('l1', r);
                      if (field === 'Date') {
                        return (
                          <td key={r}>
                            <Form.Control type="date" {...register(`l1_comments.${r}.date`)} disabled={!editable} />
                          </td>
                        );
                      } else if (field === 'Signature') {
                        return (
                          <td key={r}>
                            <Form.Select
                              {...register(`l1_comments.${r}.signatureType`)}
                              onChange={(e) => handleSignatureTypeChange('l1', r, e.target.value)}
                              className="mb-2"
                              disabled={!editable}
                            >
                              <option value="upload">Image Upload</option>
                              <option value="digital">Digital Sign</option>
                            </Form.Select>
                            {_.get(formValues, `l1_comments.${r}.signatureType`) === 'upload' && (
                              <Form.Control type="file" accept="image/*" onChange={(e) => handleImageUpload(e, 'l1', r)} className="mb-2" disabled={!editable} />
                            )}
                            {_.get(formValues, `l1_comments.${r}.signature`) && (
                              <div>
                                <img src={_.get(formValues, `l1_comments.${r}.signature`)} alt="Signature" style={{ maxWidth: '100px', marginTop: '5px' }} />
                                <Button variant="danger" size="sm" onClick={() => handleResetSignature('l1', r)} className="mt-2" disabled={!editable}>
                                  Reset Signature
                                </Button>
                              </div>
                            )}
                          </td>
                        );
                      }
                      return (
                        <td key={r}>
                          <Form.Control type="text" {...register(`l1_comments.${r}.${field.toLowerCase()}`)} disabled={!editable} />
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </Table>

            <h3 className="mt-4">5. Other Comments/Remarks</h3>
            <Form.Group className="mb-3">
              <Form.Control as="textarea" rows={4} {...register('l1_other_comments')} disabled={!canEditL1General} placeholder="Enter any additional comments or remarks for Level 1" />
            </Form.Group>

            {/* Level 1 actions */}
            {role === 'manager' && l1_status === 'MANAGER' ? (
              <div className="mt-4">
                <Button variant="success" onClick={handleSubmit(onSubmit('l1', 'approved'))} className="me-2">
                  Approve (Send to QUALITY)
                </Button>
                <Button variant="danger" onClick={handleReworkClick('l1', 'draft')}>
                  Re-Work
                </Button>
              </div>
            ) : role === 'quality' && l1_status === 'QUALITY' ? (
              <div className="mt-4">
                <Button variant="success" onClick={handleSubmit(onSubmit('l1', 'approved'))} className="me-2">
                  Final Approve
                </Button>
                <Button variant="danger" onClick={handleReworkClick('l1', 'draft')}>
                  Apply for Resubmit
                </Button>
              </div>
            ) : (
              <div className="mt-4">
                <Button variant="success" onClick={handleSubmit(onSubmit('l1', 'pending'))} disabled={submitDisabledL1}>
                  Submit Level 1
                </Button>
                <Button variant="secondary" style={{ marginLeft: '10px' }} onClick={handleReworkClick('l1', 'draft')} disabled={submitDisabledL1}>
                  Save As Draft
                </Button>
              </div>
            )}
          </Tab>

          <Tab eventKey="level2" title={`Level 2 - Commissioning (${l2_status})`} disabled={disableLevel2Tab}>
            <h3 className="mt-4">6. Commissioning Tests</h3>
            <p><strong>Note:</strong> Ensure all below points are in coordination with ANNEXURE: VCB: COMMISSIONING PART: Level 2. If all tests are found correct, select appropriate options below.</p>
            <p><strong>Note:</strong> I) Ensure the use of a 5kV Digital insulation resistance (IR) tester is recommended. Analog IR Tester is Not recommended for IR Testing.</p>
            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Functional Checks</Form.Label>
                  <Form.Select {...register('functional_checks_status')} disabled={!isLevel2Editable}>
                    <option value="">Select</option>
                    <option value="Yes">Yes</option>
                    <option value="No">No</option>
                  </Form.Select>
                </Form.Group>
                <Form.Group className="mb-3">
                  <Form.Label>Testing &amp; Commissioning</Form.Label>
                  <Form.Select {...register('testing_commissioning_status')} disabled={!isLevel2Editable}>
                    <option value="">Select</option>
                    <option value="Yes">Yes</option>
                    <option value="No">No</option>
                  </Form.Select>
                </Form.Group>
                <Form.Group className="mb-3">
                  <Form.Label>VCB Found Healthy & Ready to be Charged</Form.Label>
                  <Form.Select {...register('vcb_healthy')} disabled={!isLevel2Editable}>
                    <option value="">Select</option>
                    <option value="Yes">Yes</option>
                    <option value="No">No</option>
                  </Form.Select>
                </Form.Group>
                <Form.Group className="mb-3">
                  <Form.Label>Insulation Resistance Test Completed</Form.Label>
                  <Form.Select {...register('insulation_resistance_test')} disabled={!isLevel2Editable}>
                    <option value="">Select</option>
                    <option value="Yes">Yes</option>
                    <option value="No">No</option>
                  </Form.Select>
                </Form.Group>
              </Col>
            </Row>

            <h3 className="mt-4">7. Functional Checks</h3>
            <Table bordered>
              <thead>
                <tr>
                  <th>Sr. No.</th>
                  <th>Description</th>
                  <th>Status</th>
                  <th>Remark</th>
                </tr>
              </thead>
              <tbody>
                {[
                  'Functional check tripping and closing of VCB check from TNC control switch & manually from VCB',
                  'Functional check of Vacuum circuit breaker',
                  'Functional check of master trip (86) relay',
                  'Check the working of all indication lamps and alarms',
                  'Check the Rack-in and Rack-out alignment',
                  'Push Button & Selector Switch is operational condition',
                  'Relay Setting to be attached with SAT: Over voltage, Under voltage, Over current, Earth Fault',
                ].map((desc, index) => (
                  <tr key={index}>
                    <td>{index + 1}</td>
                    <td>{desc}</td>
                    <td>
                      <Form.Check inline label="Yes" type="radio" name={`functional_checks_${index}`} value="Yes" {...register(`functional_checks.${index}.status`)} disabled={!isLevel2Editable} />
                      <Form.Check inline label="No" type="radio" name={`functional_checks_${index}`} value="No" {...register(`functional_checks.${index}.status`)} disabled={!isLevel2Editable} />
                      <Form.Check inline label="NA" type="radio" name={`functional_checks_${index}`} value="NA" {...register(`functional_checks.${index}.status`)} disabled={!isLevel2Editable} />
                    </td>
                    <td>
                      <Form.Control type="text" {...register(`functional_checks.${index}.remark`)} disabled={!isLevel2Editable} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>

            <h3 className="mt-4">8. Test Instruments Details and Calibration Status</h3>
            <Table bordered>
              <thead>
                <tr>
                  <th>Sr. No.</th>
                  <th>Instrument Description</th>
                  <th>Instrument Serial No.</th>
                  <th>Range</th>
                  <th>Calibration Status</th>
                </tr>
              </thead>
              <tbody>
                {['IR Tester (5kV Recommended)', 'Digital Multi meter / Tong Tester'].map((desc, index) => (
                  <tr key={index}>
                    <td>{index + 1}</td>
                    <td>{desc}</td>
                    <td><Form.Control type="text" {...register(`instruments.${index}.serial`)} disabled={!isLevel2Editable} /></td>
                    <td><Form.Control type="text" {...register(`instruments.${index}.range`)} disabled={!isLevel2Editable} /></td>
                    <td><Form.Control type="text" {...register(`instruments.${index}.calibration`)} disabled={!isLevel2Editable} /></td>
                  </tr>
                ))}
              </tbody>
            </Table>

            <h3 className="mt-4">9. Testing & Commissioning (Electrical Checks / Tests)</h3>
            <h4>I) Insulation Resistance Test</h4>
            <p><strong>Note:</strong> During Megger testing, isolate neutral link, remove Capacitor Bank, space heater fuses if any.</p>
            <Table bordered>
              <thead>
                <tr>
                  <th>Sr. No.</th>
                  <th>Phase to Phase</th>
                  <th>Value (MΩ/GΩ)</th>
                  <th>Remarks</th>
                </tr>
              </thead>
              <tbody>
                {['R - Y', 'Y - B', 'B - R', 'R - E', 'Y - E', 'B - E'].map((phase, index) => (
                  <tr key={index}>
                    <td>{index + 1}</td>
                    <td>{phase}</td>
                    <td><Form.Control type="text" {...register(`insulation_resistance_results.${index}.value`)} disabled={!isLevel2Editable} /></td>
                    <td><Form.Control type="text" {...register(`insulation_resistance_results.${index}.remark`)} disabled={!isLevel2Editable} /></td>
                  </tr>
                ))}
              </tbody>
            </Table>

            <h4 className="mt-4">II) INSULATION RESISTANCE TEST : Breaker OFF - Contact Open</h4>
            <Table bordered>
              <thead>
                <tr>
                  <th>Sr. No.</th>
                  <th>Pole to Pole</th>
                  <th>Value (MΩ/ GΩ)</th>
                  <th>Remarks</th>
                </tr>
              </thead>
              <tbody>
                {['R - R1', 'Y - Y1', 'B - B1'].map((phase, index) => (
                  <tr key={index}>
                    <td>{index + 1}</td>
                    <td>{phase}</td>
                    <td><Form.Control type="text" {...register(`insulation_resistance_breaker_off.${index}.value`)} disabled={!isLevel2Editable} /></td>
                    <td><Form.Control type="text" {...register(`insulation_resistance_breaker_off.${index}.remark`)} disabled={!isLevel2Editable} /></td>
                  </tr>
                ))}
              </tbody>
            </Table>

            <h4 className="mt-4">III) Relay Setting -</h4>
            
            <h5 className="mt-3">A-Inverse Over Current Setting (Low set)</h5>
            <Table bordered>
              <thead>
                <tr>
                  <th>Sr. No.</th>
                  <th>Setting</th>
                  <th>Value</th>
                  <th>Remarks</th>
                </tr>
              </thead>
              <tbody>
                {['Ip>', 'TMS', 'Curve Normal Inverse (NI), NI-3.0'].map((setting, index) => (
                  <tr key={index}>
                    <td>{index + 1}</td>
                    <td>{setting}</td>
                    <td><Form.Control type="text" {...register(`relay_setting_a.${index}.value`)} disabled={!isLevel2Editable} /></td>
                    <td><Form.Control type="text" {...register(`relay_setting_a.${index}.remark`)} disabled={!isLevel2Editable} /></td>
                  </tr>
                ))}
              </tbody>
            </Table>

            <h5 className="mt-3">B-DT Over Current Setting (High set)</h5>
            <Table bordered>
              <thead>
                <tr>
                  <th>Sr. No.</th>
                  <th>Setting</th>
                  <th>Value</th>
                  <th>Remarks</th>
                </tr>
              </thead>
              <tbody>
                {['Ip>>', 'Time (In sec)', 'Curve Definite Time (DT)'].map((setting, index) => (
                  <tr key={index}>
                    <td>{index + 1}</td>
                    <td>{setting}</td>
                    <td><Form.Control type="text" {...register(`relay_setting_b.${index}.value`)} disabled={!isLevel2Editable} /></td>
                    <td><Form.Control type="text" {...register(`relay_setting_b.${index}.remark`)} disabled={!isLevel2Editable} /></td>
                  </tr>
                ))}
              </tbody>
            </Table>

            <h5 className="mt-3">C-Inverse Earth Fault Current Setting (Low set)</h5>
            <Table bordered>
              <thead>
                <tr>
                  <th>Sr. No.</th>
                  <th>Setting</th>
                  <th>Value</th>
                  <th>Remarks</th>
                </tr>
              </thead>
              <tbody>
                {['Ie>', 'TMS', 'Curve Normal Inverse (NI), NI-3.0'].map((setting, index) => (
                  <tr key={index}>
                    <td>{index + 1}</td>
                    <td>{setting}</td>
                    <td><Form.Control type="text" {...register(`relay_setting_c.${index}.value`)} disabled={!isLevel2Editable} /></td>
                    <td><Form.Control type="text" {...register(`relay_setting_c.${index}.remark`)} disabled={!isLevel2Editable} /></td>
                  </tr>
                ))}
              </tbody>
            </Table>

            <h5 className="mt-3">D-DT Earth Fault Setting (High set)</h5>
            <Table bordered>
              <thead>
                <tr>
                  <th>Sr. No.</th>
                  <th>Setting</th>
                  <th>Value</th>
                  <th>Remarks</th>
                </tr>
              </thead>
              <tbody>
                {['Ie>>', 'Time (In sec)', 'Curve Definite Time (DT)'].map((setting, index) => (
                  <tr key={index}>
                    <td>{index + 1}</td>
                    <td>{setting}</td>
                    <td><Form.Control type="text" {...register(`relay_setting_d.${index}.value`)} disabled={!isLevel2Editable} /></td>
                    <td><Form.Control type="text" {...register(`relay_setting_d.${index}.remark`)} disabled={!isLevel2Editable} /></td>
                  </tr>
                ))}
              </tbody>
            </Table>

            <h3 className="mt-4">10. Level 2 Commissioning Comments</h3>
            <Table bordered>
              <thead>
                <tr>
                  <th></th>
                  <th>Engineer - Contractor</th>
                  <th>Engineer - Charge Zone</th>
                  <th>Quality - Charge Zone</th>
                  <th>Optional</th>
                </tr>
              </thead>
              <tbody>
                {['Name', 'Company', 'Signature', 'Date'].map((field) => (
                  <tr key={field}>
                    <td>{field}</td>
                    {['contractor', 'charge_engineer', 'quality', 'optional'].map((r) => {
                      const editable = canEditSignature('l2', r);
                      if (field === 'Date') {
                        return (
                          <td key={r}>
                            <Form.Control type="date" {...register(`l2_comments.${r}.date`)} disabled={!editable} />
                          </td>
                        );
                      } else if (field === 'Signature') {
                        return (
                          <td key={r}>
                            <Form.Select
                              {...register(`l2_comments.${r}.signatureType`)}
                              onChange={(e) => handleSignatureTypeChange('l2', r, e.target.value)}
                              className="mb-2"
                              disabled={!editable}
                            >
                              <option value="upload">Image Upload</option>
                              <option value="digital">Digital Sign</option>
                            </Form.Select>
                            {_.get(formValues, `l2_comments.${r}.signatureType`) === 'upload' && (
                              <Form.Control type="file" accept="image/*" onChange={(e) => handleImageUpload(e, 'l2', r)} className="mb-2" disabled={!editable} />
                            )}
                            {_.get(formValues, `l2_comments.${r}.signature`) && (
                              <div>
                                <img src={_.get(formValues, `l2_comments.${r}.signature`)} alt="Signature" style={{ maxWidth: '100px', marginTop: '5px' }} />
                                <Button variant="danger" size="sm" onClick={() => handleResetSignature('l2', r)} className="mt-2" disabled={!editable}>
                                  Reset Signature
                                </Button>
                              </div>
                            )}
                          </td>
                        );
                      }
                      return (
                        <td key={r}>
                          <Form.Control type="text" {...register(`l2_comments.${r}.${field.toLowerCase()}`)} disabled={!editable} />
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </Table>

            <h3 className="mt-4">11. Level 2 Commissioning Comments: For Office</h3>
            <Form.Group className="mb-3">
              <Form.Control as="textarea" rows={4} {...register('l2_office_comments')} disabled={!canEditL2General} placeholder="Enter comments for office use" />
            </Form.Group>

            <h3 className="mt-4">12. Other Comments/Remarks</h3>
            <Form.Group className="mb-3">
              <Form.Control as="textarea" rows={4} {...register('l2_other_comments')} disabled={!canEditL2General} placeholder="Enter any additional comments or remarks for Level 2" />
            </Form.Group>

            <h3 className="mt-4">13. Observations during Commissioning / Testing (If Any)</h3>
            {['a', 'b', 'c'].map((key) => (
              <Form.Group className="mb-3" key={key}>
                <Form.Label>{key.toUpperCase()}.</Form.Label>
                <Form.Control type="text" {...register(`observations.${key}`)} disabled={!canEditL2General} />
              </Form.Group>
            ))}

            {/* Level 2 actions */}
            {role === 'manager' && l2_status === 'MANAGER' ? (
              <div className="mt-4">
                <Button variant="success" onClick={handleSubmit(onSubmit('l2', 'approved'))} className="me-2">
                  Approve (Send to QUALITY)
                </Button>
                <Button variant="danger" onClick={handleReworkClick('l2', 'draft')}>
                  Re-Work
                </Button>
              </div>
            ) : role === 'quality' && l2_status === 'QUALITY' ? (
              <div className="mt-4">
                <Button variant="success" onClick={handleSubmit(onSubmit('l2', 'approved'))} className="me-2">
                  Final Approve
                </Button>
                <Button variant="danger" onClick={handleReworkClick('l2', 'draft')}>
                  Apply for Resubmit
                </Button>
              </div>
            ) : (
              <div className="mt-4">
                <Button variant="success" onClick={handleSubmit(onSubmit('l2', 'pending'))} disabled={submitDisabledL2}>
                  Submit Level 2
                </Button>
                <Button variant="secondary" style={{ marginLeft: '10px' }} onClick={handleReworkClick('l2', 'draft')} disabled={submitDisabledL2}>
                  Save As Draft
                </Button>
              </div>
            )}
          </Tab>
        </Tabs>

        {/* Signature modal (role/stage-aware) */}
        <Modal
          show={
            showModal &&
            ((currentSignature.level === 'l1' && canEditSignature('l1', currentSignature.role)) ||
              (currentSignature.level === 'l2' && canEditSignature('l2', currentSignature.role)))
          }
          onHide={handleCloseModal}
        >
          <Modal.Header closeButton>
            <Modal.Title>Draw Signature</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <SignatureCanvas
              ref={sigCanvas}
              penColor="black"
              canvasProps={{ width: 500, height: 200, className: 'sigCanvas border' }}
            />
            <div className="mt-2">
              <Button variant="secondary" onClick={handleClearSignature} className="me-2">
                Clear
              </Button>
              <Button variant="primary" onClick={handleSaveSignature}>
                Save Signature
              </Button>
            </div>
          </Modal.Body>
        </Modal>
      </Form>

      <PunchPointsModal
        show={showPunchModal}
        onHide={() => { setShowPunchModal(false); setPendingRework(null); }}
        value={punch_points}
        onChange={(val) => setValue('punch_points', val)}
        onSave={handleSavePunchPoints}
        canEdit={canEditPunchPoints && !!pendingRework}
      />
    </div>
  );
};

VCBForm.propTypes = {
  role: PropTypes.oneOf(['engineer', 'contractor', 'manager', 'quality', 'admin']),
  userId: PropTypes.string,
};

export default VCBForm;
